import { ProjectType, ProjectTypes, TimecardClientNotesInputModes, TimecardInternalNotesInputModes } from "../../../common/constants"
import { isValidJobName } from "../../../domain/jobs/jobName"
import { PropertySelection } from "../../PropertySelector/PropertySelector"

export type NewCustomerData = {
  company: string
  firstName: string
  lastName: string
  nickname: string
  address1: string
  address2: string
  city: string
  zip: string
  countryCode: string
  state: string
  latitude?: number
  longitude?: number
  phoneType: number
  phoneNumber: string
  email: string
}

export const newCustomerValidationRules = {
  company: {required: false, maxLength: 75 },
  firstName: {required: true, maxLength: 25 },
  lastName: {required: true, maxLength: 35 },
  nickname: {required: false, maxLength: 45 },
  address1: {required: true, maxLength: 45 },
  address2: {required: false, maxLength: 45 },
  city: {required: true, maxLength: 35 },
  zip: {required: true, maxLength: 10 },
  countryCode: {required: true, maxLength: null },
  state: {required: true, maxLength: 35 },
  phoneNumber: {required: false, maxLength: 20 },
  email: {required: false, maxLength: 45 }
}

export const createEmptyCustomerData = (): NewCustomerData => ({
  company: '',
  firstName: '',
  lastName: '',
  nickname: '',
  address1: '',
  address2: '',
  city: '',
  zip: '',
  countryCode: 'US',
  state: '',
  phoneType: 0,
  phoneNumber: '',
  email: ''
})

export const isCustomerDataValid = (data: NewCustomerData): boolean => {
  for (const [key, value] of Object.entries(newCustomerValidationRules)) {
    const field = (data as any)[key]
    if (value.required && !field) {
      return false
    }
    if (value.maxLength && field.length > value.maxLength) {
      return false
    }
  }
  return true
}

export type CustomerOption = 'existing' | 'new'

export const allowCreateJob = (customerOption: CustomerOption, propertySelection: PropertySelection, data: NewCustomerData, name: string, nameRequired: boolean): boolean => {
  if (nameRequired && !isValidJobName(name)) {
    return false
  }

  if (customerOption === 'existing') {
    return propertySelection !== undefined
  }

  if (customerOption === 'new') {
    return isCustomerDataValid(data)
  }

  return false
}

export type NewJobRequest = NewCustomerData & {
  name: string
  customerId?: number
  propertyId?: number
  timecardInternalNotesInputMode: number
  timecardClientNotesInputMode: number
}

export type NewJobResponse = {
  url: string
  estimateDate: string
  salesPersonName: string
  customer: {
    displayName: string
  }
  property: {
    address1: string
  }
}

export const buildNewJobRequest = (customerOption: CustomerOption, propertySelection: PropertySelection, data: NewCustomerData, name: string): NewJobRequest => {
  if (customerOption === 'existing') {
    return {
      ...createEmptyCustomerData(),
      name: name.trim(),
      customerId: propertySelection!.customerId,
      propertyId: propertySelection!.propertyId,
      timecardInternalNotesInputMode: TimecardInternalNotesInputModes.ShowButNotRequireInput,
      timecardClientNotesInputMode: TimecardClientNotesInputModes.ShowAndNotificationOnByDefault
    }
  }

  return {
    ...data,
    name: name.trim(),
    timecardInternalNotesInputMode: TimecardInternalNotesInputModes.ShowButNotRequireInput,
    timecardClientNotesInputMode: TimecardClientNotesInputModes.ShowAndNotificationOnByDefault
  }
}

const getPath = (projectType: ProjectType) => {
  if (projectType === ProjectTypes.Maintenance) {
    return 'maintenance'
  }
  if (projectType === ProjectTypes.Ticket) {
    return 'tickets'
  }
  return 'projects'
}

export const getJobRedirect = (projectType: ProjectType, response: NewJobResponse) => {
  return `#!/${getPath(projectType)}/${response.url}`
}

const getEventType = (projectType: ProjectType) => {
  if (projectType === ProjectTypes.Maintenance) {
    return 'created-maintenance'
  }
  if (projectType === ProjectTypes.Ticket) {
    return 'created-ticket'
  }
  return 'created-project'
}

export const getIntercomEventData = (projectType: ProjectType, response: NewJobResponse) => {
  return {
    event: getEventType(projectType),
    metadata: {
      customer: response.customer.displayName,
      property: response.property.address1,
      salesperson: response.salesPersonName,
      date: response.estimateDate
    }
  }
}